/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { SchutzMassnahmen } from 'components/schutzMassnahmen'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Link, ScrollTracking } from '@ta-interaktiv/contentbricks'

const ADDITIONAL_SHARES = 328 + 319
const ARTICLE_ID = '13878467'
const whiteIconDown = require('images/thumb-down-white.png')
const whiteIconUp = require('images/thumb-up-white.png')
const blackIconDown = require('images/thumb-down-black.png')
const blackIconUp = require('images/thumb-up-black.png')
export function AppComponent() {
  const isMobile = window.innerWidth < 600
  const isTablet = window.innerWidth < 1025

  const CustomButtonComponent = ({
    type,
    onClick,
    selected,
    deactivated
  }: {
    type: string
    selected: boolean
    deactivated: boolean
    onClick: (e: React.MouseEvent) => void
  }) => {
    const [hover, setHover] = useState<[boolean, boolean]>([false, false])
    return (
      <>
        <div
          className={
            'icon ' +
            (selected ? 'selected' : '') +
            ' ' +
            (deactivated ? 'deactivated' : '')
          }
          onMouseEnter={e => {
            if (type === 'd') setHover([true, false])
            if (type === 'u') setHover([false, true])
          }}
          onMouseLeave={e => {
            setHover([false, false])
          }}
          onClick={!deactivated && !selected ? onClick : undefined}
        >
          <img
            src={
              type === 'd'
                ? selected
                  ? whiteIconDown
                  : hover[0] && !deactivated
                  ? whiteIconDown
                  : blackIconDown
                : selected
                ? whiteIconUp
                : hover[1] && !deactivated
                ? whiteIconUp
                : blackIconUp
            }
          />
          <div className='text'>
            {type === 'd' ? 'Nein, ich habe Angst!' : 'Ja, ganz klar!'}
          </div>
        </div>
      </>
    )
  }
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ThemeSwitch />

      {/* TODO Replace the following segment with your project cover */}

      <header className='ui vertical fitted segment' id='wrapper'>
        <div className='ui container' id='container'>
          <h1 className='ui header' style={{ marginBottom: 0 }}>
            Auch beim allerschlimmsten Szenario überleben 99,6 Prozent
          </h1>
          <h2 className='ui header' style={{ marginTop: 0 }}>
            <div className='small sub header'>
              Im Land herrscht eine angespannte Stimmung, viele sind besorgt
              oder haben sogar Angst vor dem Coronavirus. Wir wollen hier nicht
              verharmlosen, einige Datenvergleiche können allerdings beruhigend
              wirken.
            </div>
          </h2>
          <p className='ta author'>
            <strong>Marc Brupbacher und Patrick Vögeli</strong>
          </p>
        </div>
        <div className='ui vertical segment' style={{ paddingBottom: 0 }}>
          <div style={{ width: '100%' }} className='glitchWrapper'>
            <div className='glitch' />
          </div>
          <div className='ui container'>
            <div className='ta secondary caption'>
              Lassen sich nicht unterkriegen: Junge Menschen in Hongkong an
              einem Konzert. Foto: Reuters, 27. Februar 2020
            </div>
          </div>
        </div>
      </header>

      {/* TODO And probably remove the following segment and replace it with
       your own content */}
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <p>
            Jetzt ist nicht die richtige Zeit für Panik. Selbst wenn das
            Worst-Case-Szenario eintrifft und sich ein grosser Teil der
            Schweizer Bevölkerung mit dem neuen Coronavirus infizieren würden,
            so wie das Epidemienforscher Christian Althaus{' '}
            <Link href='https://www.nzz.ch/schweiz/experte-zum-corona-ausbruch-man-muss-jetzt-nicht-die-halbe-schweiz-unter-quarantaene-stellen-ld.1542713'>
              in der NZZ{' '}
            </Link>
            skizzierte, würden bei den prognostizierten 30’000 Toten{' '}
            <span className='highlight box'>
              doch 99,6% der Schweizerinnen und Schweizer überleben
            </span>
            . Aber derzeit deutet nichts auf so ein Szenario hin.
          </p>
          <p>
            Zum Vergleich: In der chinesischen Krisenprovinz Hubei gibt es
            offiziell rund 66’000 bestätigte Covid-19-Fälle mit 2700 Toten, in
            der Region leben über 57 Millionen Menschen. Und es wurden zuletzt
            jeden Tag weniger Fälle gemeldet. Momentan sind also etwa 0,004%
            oder rund 1 von 24’000 Einwohnern von Hubei dem Virus erlegen. Würde
            die Epidemie in der Schweiz ähnlich drastisch verlaufen wie in Hubei
            Stand heute, gäbe es hier etwa so viele Tote wie in einer normalen,
            milden Grippesaison.
          </p>
          <ScrollTracking label='content25' />
          <p>
            <span className='highlight box'>
              Im restlichen China gab es bisher 106 Tote auf 1,3 Milliarden
              Menschen, und es werden kaum mehr neue Fälle gemeldet
            </span>
            ; zuletzt waren es jeweils noch weniger als zehn am Tag.
            Gleichzeitig steigt die Zahl derjenigen, die sich vom Virus erholen.
            Von den in China mittlerweile offiziell total 79’200 Erkrankten sind
            über 39’000 inzwischen wieder geheilt, also mehr als die Hälfte.
          </p>

          <div className='iframeContainer'>
            <AutofitIFrame
              title='Immer weniger Covid-19-Kranke'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/6oy79/9/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>

          <p>
            <span className='highlight box'>
              Die Coronavirus-Epidemie hat nach Ansicht der WHO ihren Höhepunkt
              in ihrem Ursprungsort und der am schwersten betroffenen
              Millionenstadt Wuhan bereits am 2. Februar überschritten
            </span>
            . Allerdings gibt es noch keine Entwarnung. Denn nun steigen die
            Infektionsfälle dafür ausserhalb Chinas stark an, vor allem in
            Südkorea, dem Iran und Italien. Es kann immer noch zu einer Pandemie
            kommen, die WHO schätzt das Risiko als «sehr hoch» ein.
          </p>

          <div className='iframeContainer'>
            <AutofitIFrame
              title='Neue Infektionen in China rückläufig'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/cL4E3/5/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>

          <p>
            Nicht alle Bevölkerungsschichten sind gleich gefährdet. Kinder und
            Jugendliche können zwar erkranken, allerdings passiert es deutlich
            seltener als bei Erwachsenen. Bei einer Auswertung von mehr als{' '}
            <Link href='https://interaktiv.tagesanzeiger.ch/2020/coronavirus-analyse-sterblichkeit/?nosome'>
              44’000 Fällen aus China{' '}
            </Link>{' '}
            war nur ein Prozent der Betroffenen unter zehn Jahre alt.{' '}
            <span className='highlight box'>
              Unter den ersten 1000 Todesfällen in China befand sich nur eine
              Person zwischen zehn und 19 Jahren
            </span>
            . Abgesehen davon scheinen Kinder das Virus auch nur selten
            weiterzugeben. «Wir haben bislang keinen Fall beobachtet, bei dem
            ein Kind einen Erwachsenen infiziert hat», sagte WHO-Experte Bruce
            Aylward.
          </p>

          <div className='iframeContainer'>
            <AutofitIFrame
              title='Kaum Krankheitsfälle und Tote bei jungen Personen'
              aria-label='Interactive line chart'
              src='https://datawrapper.dwcdn.net/6ATwF/8/'
              height={isMobile ? '1180' : '1120'}
              width='581'
            />
          </div>

          <ScrollTracking label='content50' />

          <p>
            Die im chinesischen Fachblatt «Chinese Journal of Epidemiology»
            publizierte{' '}
            <Link href='http://weekly.chinacdc.cn/en/article/id/e53946e2-c6c4-41e9-9a9b-fea8db1a8f51'>
              Studie{' '}
            </Link>{' '}
            untersuchte Covid-19-Fälle in Festlandchina bis zum 11. Februar
            2020.{' '}
            <span className='highlight box'>
              Bei 80,9% der Infizierten nahm die Krankheit einen milden Verlauf
            </span>
            . Und selbst bei den schweren Fällen (13,8%) gab es keine Toten, nur
            bei den kritischen (4,7%).
          </p>

          <div className='iframeContainer'>
            <AutofitIFrame
              title='Bei vier von fünf Personen nimmt Covid-19 einen milden Verlauf'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/fReNR/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>

          <p>
            Während Patienten ohne Vorerkrankungen eine Sterblichkeitsrate von
            0,9% aufweisen, steigt die Rate bei Patienten mit
            Herz-Kreislauf-Erkrankungen, Diabetes, chronischen
            Atemwegserkrankungen, Bluthochdruck und Krebs drastisch an.
          </p>
          <p>
            Der Römer Chefinfektiologe hat das so formuliert:{' '}
            <span className='highlight box'>
              «Menschen, die in diesen Tagen in Italien verstorben sind, sind
              mit dem Coronavirus verstorben, nicht wegen des Coronavirus.»
            </span>
          </p>
          <p>
            Trotzdem darf man das Virus natürlich nicht unterschätzen: Obwohl es
            viele milde Verläufe von Covid-19 gibt, bleibt das Virus gefährlich,
            auch jüngere und gesunde Menschen können daran sterben. Denn auch
            wenn die Sterblichkeit wegen der Anzahl leichter Fälle, die nicht
            bekannt wurden und als Dunkelziffer gelten, auf 0,7 bis 1 Prozent
            sinken würde, wovon die{' '}
            <Link href='https://github.com/calthaus'>meisten Experten </Link>{' '}
            ausgehen, ist diese deutlich höher als bei der Grippe mit 0,02 bis
            0,4 Prozent – zudem ist Sars-CoV-2 auch deutlich ansteckender.
            Influenzafälle gibt es pro Jahr weltweit bis zu 90 Millionen, dabei
            sterben 200'000 bis 650'000 Personen. Auch junge Personen. «Die
            Grippe ist eine schwere Krankheit. Letzte Woche ist bei uns eine
            junge Frau an Grippe gestorben», sagt Infektiologe Pietro Vernazza,
            Chefarzt am Kantonsspital St. Gallen, der{' '}
            <Link href='https://www.woz.ch/2009/coronavirus/das-virus-hat-null-interesse-seinen-wirt-zu-toeten?fbclid=IwAR2LIhAj7IKJT-GjLwQGbV96Ll_adWomByWv17RxwVMi0DCihb0eyLy-GwE'>
              «Wochenzeitung» (WOZ){' '}
            </Link>
            .
          </p>
          <p>
            Obwohl die Gefährlichkeit der Grippe bekannt ist und auch in der
            Schweiz daran jährlich mehrere Hundert, bei einer schweren
            Grippesaison wie 2015 sogar mehrere Tausend Personen sterben,
            scheint uns die Grippe kaum mehr Angst zu machen. Es impfen sich
            jährlich lediglich 20 Prozent der Bevölkerung dagegen. Dabei: Mit
            einer Impfung schützt man nicht nur sich selbst, sondern alle
            anderen auch.
          </p>
          <ScrollTracking label='content75' />
          <p>
            Warum macht uns Covid-19 dann solche Angst? Sie ist auch deshalb so
            erschreckend, weil die Krankheit neu ist.{' '}
            <span className='highlight box'>
              An die Grippe haben wir uns gewöhnt, obwohl auch sie jedes Jahr
              tödlich zuschlägt
            </span>
            . Wir haben uns sogar so sehr gewöhnt, dass viele die von ihr
            ausgehende Gefahr unterschätzen. «Es irritiert mich, wie
            verantwortungslos wir als Gesellschaft mit der Grippe umgehen.{' '}
            <span className='highlight box'>
              Klar, die Sterberate ist bei der Grippe fünf- oder zehnmal tiefer
              als beim Coronavirus. Aber dadurch, dass sie jedes Jahr in einer
              neuen Version kommt, richtet sie insgesamt einen viel grösseren
              Schaden an»
            </span>
            , so der St. Galler Chefarzt Vernazza.
          </p>
          <p>
            Ja, die Schweiz steht vor einer der grössten gesundheitlichen
            Notlagen ihrer jüngeren Geschichte. Das hat aber hauptsächlich damit
            zu tun, dass es in den letzten Jahrzehnten keine grössere
            gesundheitliche Notlage gab.
          </p>
          <p>
            <span className='highlight box'>
              Der allergrösste Teil der Bevölkerung wird bei einer Infektion die
              Krankheit sehr gut überstehen
            </span>
            . Ganz junge Menschen müssen sich überhaupt keine Sorgen machen. Es
            geht jetzt darum, kühlen Kopf zu bewahren, keine Panik zu verbreiten
            und besonders zwei Gruppen zu schützen: ältere Menschen und solche
            mit chronischen Krankheiten. Darum sollte jeder diszipliniert die
            Anweisungen der Gesundheitsbehörden, Ärzte und Infektiologen
            einhalten, Hände waschen, aufmerksam sein, bei Krankheitssymptomen
            zu Hause bleiben.
          </p>
          <SchutzMassnahmen />
          <p>
            Mit gesundem Menschenverstand können alle dazu beitragen, dass sich
            das Virus nur langsam ausbreitet, nie zu viele Personen aufs Mal
            krank sind und das Gesundheitssystem dadurch nicht überlastet wird.
            «Wir werden die Ausbreitung des neuen Coronavirus aber nicht
            verhindern können. Niemand ist immun.», sagt Infektiologe Vernazza.
            Die Krankheit werde den Grossteil der Bevölkerung erfassen.
          </p>
          <p>
            Auch der deutsche Virologe{' '}
            <Link href='https://www.spiegel.de/wissenschaft/medizin/coronavirus-krisenort-heinsberg-kann-covid-19-infektionsketten-nicht-mehr-nachvollziehen-a-d9bd4fba-dc88-404b-b67f-431d6df7b92c'>
              Christian Drosten{' '}
            </Link>
            von der Berliner Charité erwartet hohe Infektionszahlen. «Es werden
            sich wahrscheinlich 60 bis 70 Prozent infizieren, aber wir wissen
            nicht, in welcher Zeit», sagte er. «Das kann durchaus zwei Jahre
            dauern oder sogar noch länger.»{' '}
            <span className='highlight box'>
              Problematisch werde das Infektionsgeschehen nur, wenn es in
              komprimierter, kurzer Zeit auftrete.
            </span>
          </p>
        </div>

        {/* <div className='ui text container' style={{ paddingTop: '3rem' }}>
          <h3 className='infographic header'>Sind Sie dabei?</h3>fdfd
          <div className='subtitle'>
            Ich halte mich strikt an die Hygieneregeln, stürme nicht wegen einem
            leichten Kratzen im Hals die Arztpraxis, hamstere keine Esswaren
            oder Apotheken-Artikel und verbreite keine Panik.
          </div>
          <MiniVoting
            elementID='partOfCoronaDontPanic'kjh
            articleID={ARTICLE_ID}
            relative
            CustomButtons={CustomButtonComponent}d
            hideMessage
            hideBar
            notRevertable
          />
        </div> */}
      </div>

      {/* TODO Add your credits in this component */}
      <Footer
        articleId={ARTICLE_ID}
        additionalShares={ADDITIONAL_SHARES}
        credits={[
          [
            'Konzept, Recherche und Umsetzung',
            'Marc Brupbacher, Patrick Vögeli'
          ]
        ]}
        questions={['Wie informativ war der Beitrag?']}
      >
        <div
          className='ui container'
          style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
        >
          <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={[
              '29899036',
              '11435150',
              '13939717',
              '19483314',
              '15650041',
              '31092907',
              '10448437'
            ]}
          />
        </div>
      </Footer>
    </div>
  )
}
